import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { withRouter, RouteComponentProps, useLocation } from 'react-router-v5';
import { compact } from 'lodash';
import {
  logout as logoutAction,
  testError as testErrorAction,
} from 'spw-redux/modules/auth/actions';
import { openFeedbackModal as openFeedbackModalAction } from 'spw-redux/modules/feedback/actions';
import { IAppState } from 'spw-redux/reducer';
import { IUser } from 'spw-redux/modules/user/types';
import { IProject, UserRoleInProject } from 'spw-redux/modules/projects/types';
import { fetchUser as fetchUserAction } from 'spw-redux/modules/user/actions';
import { compareVersion as compareVersionAction } from 'spw-redux/modules/UI/messages/actions';
import { DownloadProgressIndicator } from 'spw-programming-projects/downloads/download-progress-indicator';
import { isProdEnv } from 'spw-programming-projects/project-config';
import FeedbackDialog from 'spw-components/dialogs/feedback';
import ToastMessage from 'spw-components/toast-message';
import { UnstyledLink, UnstyledReactLink } from 'spw-shared/globalComponents';

import SpacePlanWizardLogoSrc from 'spw-static/images/space-plan-wizard-logo.svg';
import SpacePlanWizardTextLogoSrc from 'spw-static/images/space-plan-wizard.svg';
import DownloadIncompleteMessages from 'spw-programming-projects/downloads/download-incomplete-messages';
import WarningMessage from 'spw-components/collab/warning-message';
import useCheckFloorplanOperationBeforeCalling from 'spw-shared/hooks/use-check-selection-mode-before-calling';
import { APIToken } from 'spw-redux/modules/auth/types';
import { getAriaLabelForSensitiveInfo } from 'spw-shared/utils';
import { selectCurrentBechmarkReport } from 'spw-redux/modules/benchmark-reports/selectors';
import { UserRoleInReport } from 'spw-redux/modules/benchmark-reports/types';
import UnitConversionToggle from 'spw-components/unit-conversion/unit-conversion-toggle';
import { selectCurrentOrganization } from 'spw-redux/modules/organization/selectors';
import { selectNumberOfDownloadsInProgress } from 'spw-redux/modules/downloads/selectors';
import HeaderNotifications from './header-notifications';
import {
  Gradient,
  SpacePlanWizardLogo,
  HeaderBox,
  SectionWrapper,
  LinkStyled,
  MenuColumn,
  EmailRow,
  LogoutText,
  LogoutRow,
  LinksWrapper,
  SpacePlanWizardTextLogo,
  HeaderAppTitle,
  StickyHeaderContainer,
  HeaderAvatar,
  HEADER_HEIGHT_IN_PX,
} from './styles';
import Help from './help';
import AIFeaturesPopup from './ai-features-popup';

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      logout: logoutAction,
      openFeedbackModal: openFeedbackModalAction,
      fetchUser: fetchUserAction,
      compareVersion: compareVersionAction,
      testError: testErrorAction,
    },
    dispatch
  );

const mapStateToProps = (state: IAppState) => ({
  open: state.feedbackReducer.open,
  user: state.userReducer.user,
  apiToken: state.authReducer.apiToken,
  currentProject: state.projectsReducer.currentProject,
  versionFetchComplete: state.messagesReducer.versionFetchComplete,
});

interface IHeader {
  logout: (endSession: boolean) => void;
  open: boolean;
  user: IUser;
  apiToken: APIToken | null;
  currentProject: IProject;
  openFeedbackModal: (open: boolean) => void;
  fetchUser: () => void;
  compareVersion: () => void;
  versionFetchComplete: boolean;
  testError: () => void;
}

const HeaderLink = ({
  href,
  text,
  dataTestId,
}: {
  text: string;
  href: string;
  dataTestId?: string;
}) => {
  const location = useLocation();

  return (
    <LinkStyled
      className={location.pathname.startsWith(href) ? 'active' : ''}
      to={href}
      data-testid={dataTestId}
    >
      <div className="content">{text}</div>
    </LinkStyled>
  );
};

const useStyles = makeStyles({
  userMenuPaper: {
    marginTop: '8px',
  },
});

const discardedRoutes = ['login', 'auth'];

const Header = (props: IHeader & RouteComponentProps) => {
  const {
    location: { pathname },
    logout,
    user,
    apiToken,
    currentProject,
    fetchUser,
    compareVersion,
    versionFetchComplete,
    testError,
  } = props;

  const dispatch = useDispatch();
  const currentBenchmarkReport = useSelector(selectCurrentBechmarkReport);
  const organization = useSelector(selectCurrentOrganization);
  const numberOfDownloadsInProgress = useSelector(
    selectNumberOfDownloadsInProgress
  );

  const undefinedMethodToTestSentry = useCallback(() => {
    dispatch(testError);
    throw new Error('Function not implemented.');
  }, [dispatch, testError]);

  const { 1: currentLocation } = pathname.split('/');

  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();

  const handleOpenAvatarMenu = useCheckFloorplanOperationBeforeCalling(
    (event: SyntheticEvent) => {
      setAnchorEl(event.target);
    }
  );

  const handleClickAvatar = (event: SyntheticEvent) => {
    event.persist();
    handleOpenAvatarMenu(event);
  };

  const handleCloseAvatar = () => {
    setAnchorEl(null);
  };

  const { id, email, hasAvatar, thumbnailS3Path, isOrgAdmin } = user;

  useEffect(() => {
    if (
      apiToken?.apiToken &&
      ((email === '' && !discardedRoutes.includes(currentLocation)) || !user)
    ) {
      fetchUser();
      compareVersion();
    }
  }, [
    compareVersion,
    currentLocation,
    email,
    fetchUser,
    user,
    apiToken?.apiToken,
  ]);
  if (!versionFetchComplete || !user?.email) return null;

  const routes = compact([
    { text: 'Home', href: '/home' },
    isOrgAdmin && user.isSuperAdmin && { text: 'Account', href: '/account' },
    { text: 'Clients', href: '/clients' },
    { text: 'Projects', href: '/projects', dataTestId: 'projects_list' },
    organization?.hasAccessToBenchmarking && {
      text: 'Benchmark Reports',
      href: '/benchmark-reports',
      dataTestId: 'benchmark_reports_list',
    },
    { text: 'Teams', href: '/teams' },
    { text: 'Users', href: '/users' },
  ]);

  const userProfilePageUrl = `/users/${user.id}`;

  const projectViewOnlyWarning = currentProject?.currentUserRole ===
    UserRoleInProject.viewer && (
    <WarningMessage>Join Project or Clone to edit.</WarningMessage>
  );

  const reportViewOnlyWarning = currentBenchmarkReport?.currentUserRole ===
    UserRoleInReport.viewer &&
    pathname.includes('benchmark-reports/') && (
      <WarningMessage>Join Report or Save As to edit.</WarningMessage>
    );
  const downloadProgressIndicator = numberOfDownloadsInProgress ? (
    <DownloadProgressIndicator />
  ) : null;
  const AIFeaturesBadgeIsVisible =
    !projectViewOnlyWarning &&
    !reportViewOnlyWarning &&
    !downloadProgressIndicator;

  return (
    <>
      <div style={{ height: HEADER_HEIGHT_IN_PX }} />
      <StickyHeaderContainer
        data-cy="app-header-container"
        data-testid="app-header-container"
      >
        <HeaderBox>
          <SectionWrapper style={{ flexBasis: '25%' }}>
            <SpacePlanWizardLogo src={SpacePlanWizardLogoSrc} />
            <HeaderAppTitle>
              <SpacePlanWizardTextLogo src={SpacePlanWizardTextLogoSrc} />
            </HeaderAppTitle>
          </SectionWrapper>
          <SectionWrapper style={{ flex: 1 }}>
            <LinksWrapper>
              {routes.map((route) => (
                <HeaderLink key={route.text} {...route} />
              ))}
            </LinksWrapper>
          </SectionWrapper>
          <SectionWrapper>
            {projectViewOnlyWarning}
            {reportViewOnlyWarning}
            {downloadProgressIndicator}
            <DownloadIncompleteMessages />
            {AIFeaturesBadgeIsVisible ? <AIFeaturesPopup /> : null}
            {organization?.hasAccessToUnitConversion ? (
              <UnitConversionToggle />
            ) : null}

            <HeaderNotifications />
            <Help />
            <HeaderAvatar
              testingPrefix="header"
              onClick={handleClickAvatar}
              hasAvatar={hasAvatar}
              avatarPath={thumbnailS3Path}
              userId={id}
            />
          </SectionWrapper>
          {anchorEl && (
            <Popover
              id="Avatar popover"
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseAvatar}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              disableScrollLock
              PaperProps={{
                className: classes.userMenuPaper,
                'aria-label': getAriaLabelForSensitiveInfo({
                  context: 'App Header',
                  desc: 'Pop Up',
                  id: '',
                }),
              }}
            >
              <MenuColumn
                aria-label={getAriaLabelForSensitiveInfo({
                  context: 'App Header',
                  desc: 'Pop Up',
                  id: '',
                })}
              >
                <EmailRow
                  aria-label={getAriaLabelForSensitiveInfo({
                    context: 'App Header',
                    desc: 'User Email',
                    id: user.id,
                  })}
                >
                  {email}
                </EmailRow>
                <EmailRow>
                  <UnstyledReactLink
                    to={userProfilePageUrl}
                    onClick={handleCloseAvatar}
                  >
                    Profile
                  </UnstyledReactLink>
                </EmailRow>
                {user.isSuperAdmin ? (
                  <EmailRow>
                    <LogoutText>
                      <UnstyledLink href="/admin">Super Admin</UnstyledLink>
                    </LogoutText>
                  </EmailRow>
                ) : null}
                {!isProdEnv && (
                  <EmailRow
                    style={{ cursor: 'pointer' }}
                    onClick={() => undefinedMethodToTestSentry()}
                  >
                    Test Sentry
                  </EmailRow>
                )}
                <LogoutRow
                  onClick={() => {
                    setAnchorEl(null);
                    logout(true);
                  }}
                >
                  <LogoutText>LOGOUT</LogoutText>
                </LogoutRow>
              </MenuColumn>
            </Popover>
          )}
          <FeedbackDialog />
        </HeaderBox>
      </StickyHeaderContainer>
      <Gradient />
      <ToastMessage />
    </>
  );
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));
